import { Node, Edge } from 'reactflow';
import { calculateExecutionOrder } from './executionFlow';

/**
 * Auto-layout nodes horizontally based on execution order
 * @param nodes - All nodes in the pipeline
 * @param edges - All edges in the pipeline
 * @returns Updated nodes with new positions
 */
export function autoLayoutNodes(nodes: Node[], edges: Edge[]): Node[] {
  const orderMap = calculateExecutionOrder(nodes, edges);
  
  console.log('🎨 AUTO-LAYOUT: Starting layout');
  console.log('🎨 AUTO-LAYOUT: Order map:', Array.from(orderMap.entries()));
  
  // Find the input node to use as the starting point
  const inputNode = nodes.find((n) => n.id === 'input');
  const startX = inputNode ? inputNode.position.x : 100;
  const startY = inputNode ? inputNode.position.y : 200;
  
  console.log('🎨 AUTO-LAYOUT: Input node at x:', startX, 'y:', startY);
  
  // Layout constants (all nodes are left-anchored)
  const NODE_WIDTH = 280; // Width of processor nodes in pixels
  const GAP = 55; // Desired gap between nodes (half of original 110px)
  const HORIZONTAL_SPACING = NODE_WIDTH + GAP; // Distance between left edges (335px)
  const VERTICAL_SPACING = 300; // Spacing between sub-agent chains

  // Separate main pipeline nodes from sub-agent nodes
  const mainNodes = nodes.filter((n) => !n.data.orchestratorNodeId);
  const subAgentNodes = nodes.filter((n) => n.data.orchestratorNodeId);
  
  console.log('🎨 AUTO-LAYOUT: All nodes:', nodes.map(n => ({ id: n.id, orchestratorNodeId: n.data.orchestratorNodeId, subAgentName: n.data.subAgentName })));
  console.log('🎨 AUTO-LAYOUT: Main nodes:', mainNodes.map(n => n.id));
  console.log('🎨 AUTO-LAYOUT: Sub-agent nodes:', subAgentNodes.map(n => n.id));

  // Collect orphan main nodes (nodes not in execution chain and not sub-agents)
  const orphanNodes = mainNodes.filter((n) => n.id !== 'input' && !orderMap.has(n.id));
  
  console.log('🎨 AUTO-LAYOUT: Orphan main nodes:', orphanNodes.map(n => n.id));
  
  // Find the rightmost connected node to position orphans after it
  const connectedNodes = mainNodes.filter((n) => orderMap.has(n.id));
  const maxOrder = connectedNodes.length > 0 
    ? Math.max(...Array.from(orderMap.values())) 
    : 0;
  
  console.log('🎨 AUTO-LAYOUT: Max order:', maxOrder);

  // Layout main pipeline nodes
  const layoutedMainNodes = mainNodes.map((node) => {
    // Skip system input node - it should stay in its default position
    if (node.id === 'input') {
      return node;
    }
    
    const order = orderMap.get(node.id);
    
    if (order !== undefined) {
      // Node is in execution chain - use its order
      // Input is order 0 at startX, order 1 at startX + HORIZONTAL_SPACING, etc.
      const newX = startX + (order * HORIZONTAL_SPACING);
      console.log(`🎨 AUTO-LAYOUT: ${node.id} (order ${order}): x = ${startX} + (${order} * ${HORIZONTAL_SPACING}) = ${newX}`);
      return {
        ...node,
        position: {
          x: newX,
          y: startY,
        },
      };
    } else {
      // Orphan node - position after connected nodes in a row
      const orphanIndex = orphanNodes.indexOf(node);
      const orphanOrder = maxOrder + 1 + orphanIndex;
      const newX = startX + (orphanOrder * HORIZONTAL_SPACING);
      console.log(`🎨 AUTO-LAYOUT: ${node.id} (orphan, order ${orphanOrder}): x = ${startX} + (${orphanOrder} * ${HORIZONTAL_SPACING}) = ${newX}`);
      return {
        ...node,
        position: {
          x: newX,
          y: startY,
        },
      };
    }
  });
  
  // Layout sub-agent chains
  const layoutedSubAgentNodes: Node[] = [];
  
  // Group sub-agents by orchestrator
  const subAgentsByOrchestrator: Record<string, Node[]> = {};
  subAgentNodes.forEach((node) => {
    const orchestratorId = node.data.orchestratorNodeId as string;
    if (!subAgentsByOrchestrator[orchestratorId]) {
      subAgentsByOrchestrator[orchestratorId] = [];
    }
    subAgentsByOrchestrator[orchestratorId].push(node);
  });
  
  // Layout each orchestrator's sub-agents
  Object.entries(subAgentsByOrchestrator).forEach(([orchestratorId, subAgents]) => {
    // Find orchestrator position
    const orchestratorNode = layoutedMainNodes.find((n) => n.id === orchestratorId);
    if (!orchestratorNode) {
      console.log(`🎨 AUTO-LAYOUT: Orchestrator ${orchestratorId} not found, keeping sub-agent positions`);
      layoutedSubAgentNodes.push(...subAgents);
      return;
    }
    
    console.log(`🎨 AUTO-LAYOUT: Laying out sub-agents for orchestrator ${orchestratorId} at (${orchestratorNode.position.x}, ${orchestratorNode.position.y})`);
    
    // Group by sub-agent chain name
    const chainsByName: Record<string, Node[]> = {};
    subAgents.forEach((node) => {
      const chainName = (node.data.subAgentName as string) || 'default';
      if (!chainsByName[chainName]) {
        chainsByName[chainName] = [];
      }
      chainsByName[chainName].push(node);
    });
    
    // Layout each chain
    let chainIndex = 0;
    Object.entries(chainsByName).forEach(([chainName, chainNodes]) => {
      const baseX = orchestratorNode.position.x + 350; // To the right of orchestrator
      const baseY = orchestratorNode.position.y + 450 + (chainIndex * VERTICAL_SPACING); // Well below orchestrator
      
      console.log(`🎨 AUTO-LAYOUT: Chain "${chainName}" (index ${chainIndex}) starts at (${baseX}, ${baseY})`);
      
      // Position nodes in this chain horizontally
      chainNodes.forEach((node, nodeIndex) => {
        const newX = baseX + (nodeIndex * HORIZONTAL_SPACING);
        console.log(`🎨 AUTO-LAYOUT: ${node.id} (chain step ${nodeIndex}): x = ${baseX} + (${nodeIndex} * ${HORIZONTAL_SPACING}) = ${newX}`);
        
        layoutedSubAgentNodes.push({
          ...node,
          position: {
            x: newX,
            y: baseY,
          },
        });
      });
      
      chainIndex++;
    });
  });
  
  const result = [...layoutedMainNodes, ...layoutedSubAgentNodes];
  console.log('🎨 AUTO-LAYOUT: Final positions:', result.map(n => ({ id: n.id, x: n.position.x, y: n.position.y })));
  return result;
}

